import {
  useGetClaimReadinessAdminQuery,
  useGetClaimReadinessQuery,
  useGetOrgReadinessAdminQuery,
  useGetOrgReadinessQuery,
  useGetPayerReadinessAdminQuery,
  useGetPayerReadinessQuery,
} from './api';

/**
 * Hook to get organization-level billing readiness (provider)
 */
export function useOrgReadiness(options?: { skip?: boolean }) {
  const result = useGetOrgReadinessQuery(undefined, {
    skip: options?.skip,
  });

  return {
    readiness: result.data || null,
    isReady: result.data?.billing_entities?.every(e => e.is_complete) || false,
    billingEntities: result.data?.billing_entities || [],
    enrollmentSummary: result.data?.enrollment_summary || {},
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get organization-level billing readiness (admin)
 */
export function useOrgReadinessAdmin(
  organizationId: string | null,
  options?: { skip?: boolean },
) {
  const result = useGetOrgReadinessAdminQuery(
    { organizationId: organizationId! },
    {
      skip: !organizationId || options?.skip,
    },
  );

  return {
    readiness: result.data || null,
    isReady: result.data?.billing_entities?.every(e => e.is_complete) || false,
    billingEntities: result.data?.billing_entities || [],
    enrollmentSummary: result.data?.enrollment_summary || {},
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get payer-level readiness (provider)
 */
export function usePayerReadiness(
  billingEntityId: string | null,
  payerId: string | null,
  options?: { skip?: boolean },
) {
  const result = useGetPayerReadinessQuery(
    {
      billingEntityId: billingEntityId!,
      payerId: payerId!,
    },
    {
      skip: !billingEntityId || !payerId || options?.skip,
    },
  );

  return {
    readiness: result.data || null,
    canSubmit: result.data?.can_submit || false,
    blockers: result.data?.blockers || [],
    actionRequired: result.data?.action_required || null,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get payer-level readiness (admin)
 */
export function usePayerReadinessAdmin(
  organizationId: string | null,
  billingEntityId: string | null,
  payerId: string | null,
  options?: { skip?: boolean },
) {
  const result = useGetPayerReadinessAdminQuery(
    {
      organizationId: organizationId!,
      billingEntityId: billingEntityId!,
      payerId: payerId!,
    },
    {
      skip: !organizationId || !billingEntityId || !payerId || options?.skip,
    },
  );

  return {
    readiness: result.data || null,
    canSubmit: result.data?.can_submit || false,
    blockers: result.data?.blockers || [],
    actionRequired: result.data?.action_required || null,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get claim-level readiness (provider)
 */
export function useClaimReadiness(claimId: string | null, options?: { skip?: boolean }) {
  const result = useGetClaimReadinessQuery(
    { claimId: claimId! },
    {
      skip: !claimId || options?.skip,
    },
  );

  return {
    readiness: result.data || null,
    canSubmit: result.data?.can_submit || false,
    canGenerate: result.data?.can_generate || false,
    blockers: result.data?.blockers || [],
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get claim-level readiness (admin)
 */
export function useClaimReadinessAdmin(claimId: string | null, options?: { skip?: boolean }) {
  const result = useGetClaimReadinessAdminQuery(
    { claimId: claimId! },
    {
      skip: !claimId || options?.skip,
    },
  );

  return {
    readiness: result.data || null,
    canSubmit: result.data?.can_submit || false,
    canGenerate: result.data?.can_generate || false,
    blockers: result.data?.blockers || [],
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}
